import { useMemo } from 'react';

import { getDateRangeFromPeriod } from '@/components';
import type { PieChartItem } from '@/components/PieChart/types';
import { useBudgetsQuery } from '@/store/expenses-store/budget/budget.store';
import { useInvoice } from '@/store/expenses-store/invoices/invoices.store.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { type Category, MONTHS } from '@pages/BudgetDetail/BudgetDetail.constants.tsx';
import { plansByCategoryId, toBudgetCategories } from '@pages/BudgetDetail/BudgetDetail.utils.ts';
import { useBudgetTransactions } from '@pages/BudgetDetail/useBudgetTransactions.ts';
import { aggregateSpendingByCategory } from '@pages/Expenses/helpers/aggregateSpending';
import { isInflow } from '@pages/Expenses/TransactionsPage/utils/transactionColors.ts';
import type { MonthlySpend } from '@pages/Manage/components/FinancialStatusCard.tsx';

export type InvoicesToPayState = 'overdue' | 'unpaid' | 'clear';

export type InvoicesToPay = {
  state: InvoicesToPayState;
  total: number;
  sublabel: string;
};

export const useInvoicesToPay = (): InvoicesToPay => {
  const invoices = useInvoice((state) => state.invoices);
  const now = useMemo(() => new Date(), []);

  return useMemo(() => {
    const outstanding = invoices.filter((invoice) => invoice.status !== 'processed');

    const overdue = outstanding.filter((invoice) => {
      const due = invoice.detail?.dueDate as string | undefined;
      if (!due) return false;
      const dueDate = new Date(due);
      return !Number.isNaN(dueDate.getTime()) && dueDate < now;
    });

    const total = outstanding.reduce((sum, invoice) => {
      const amount = Math.abs(Number.parseFloat(invoice.amount?.number ?? '0'));
      return Number.isNaN(amount) ? sum : sum + amount;
    }, 0);

    const state: InvoicesToPayState =
      overdue.length > 0 ? 'overdue' : outstanding.length > 0 ? 'unpaid' : 'clear';

    const sublabel =
      state === 'overdue'
        ? `${overdue.length} overdue · ${outstanding.length} to pay`
        : state === 'unpaid'
          ? `${outstanding.length} invoice${outstanding.length === 1 ? '' : 's'} to pay`
          : 'Nothing due';

    return { state, total, sublabel };
  }, [invoices, now]);
};

export const useManageFinance = ({ budgetsEnabled }: { budgetsEnabled: boolean }) => {
  const { transactions, transactionCategories, loading, error, getTransactionsCategories } =
    useTransactionsStore();

  const year = new Date().getFullYear().toString();
  const yearNumber = Number(year);

  const dateRange = useMemo(
    () => getDateRangeFromPeriod({ month: 'January', year, period: 'yearly' }),
    [year],
  );

  const now = useMemo(() => new Date(), []);
  const currentMonth = now.getMonth();

  const {
    rows: txRows,
    categories: txCategories,
    refreshTransactions,
  } = useBudgetTransactions(dateRange);

  const retry = () => {
    void getTransactionsCategories();
    void refreshTransactions();
  };

  const { data: budgets, isPending: budgetsPending } = useBudgetsQuery(yearNumber, {
    enabled: budgetsEnabled,
  });

  // The plan arrives on its own request: until it does, every category reads as
  // unplanned and the status card as "$0 planned".
  const budgetsLoading = loading || (budgetsEnabled && budgetsPending);

  const budgetByCategory = useMemo(() => plansByCategoryId(budgets?.items), [budgets]);

  const budgetCategories = useMemo<Category[]>(
    () => toBudgetCategories(txCategories, txRows, budgetByCategory),
    [txCategories, txRows, budgetByCategory],
  );

  const categories = useMemo(
    () =>
      aggregateSpendingByCategory(transactions, transactionCategories).map((row) => ({
        title: row.title,
        sum: row.spent,
        icon: row.icon,
        color: row.color,
        slug: row.slug,
        categoryId: row.category?.id,
      })),
    [transactions, transactionCategories],
  );

  const items: PieChartItem[] = useMemo(
    () =>
      categories.map((category, index) => ({
        id: index + 1,
        title: category.title,
        sum: category.sum,
        icon: category.icon,
        color: category.color,
        categoryId: category.categoryId,
      })),
    [categories],
  );

  const totalPlanned = useMemo(
    () => budgetCategories.reduce((sum, cat) => sum + cat.planned, 0),
    [budgetCategories],
  );

  const spentBudget = useMemo(
    () => budgetCategories.reduce((sum, cat) => sum + cat.spent, 0),
    [budgetCategories],
  );

  const spentYear = useMemo(
    () => categories.reduce((sum, category) => sum + category.sum, 0),
    [categories],
  );

  const spentMonth = useMemo(
    () =>
      transactions.reduce((sum, tx) => {
        if (isInflow(tx.type)) return sum;
        const date = new Date(tx.created_at);
        if (Number.isNaN(date.getTime())) return sum;
        if (date.getFullYear() !== now.getFullYear() || date.getMonth() !== currentMonth) {
          return sum;
        }
        const amount = Math.abs(Number.parseFloat(tx.amount?.number ?? '0'));
        return Number.isNaN(amount) ? sum : sum + amount;
      }, 0),
    [transactions, now, currentMonth],
  );

  const monthlyYtd = useMemo<MonthlySpend[]>(() => {
    const totals = new Array<number>(currentMonth + 1).fill(0);

    for (const tx of transactions) {
      if (isInflow(tx.type)) continue;
      const date = new Date(tx.created_at);
      if (Number.isNaN(date.getTime())) continue;
      if (date.getFullYear() !== now.getFullYear() || date.getMonth() > currentMonth) continue;
      const amount = Math.abs(Number.parseFloat(tx.amount?.number ?? '0'));
      if (!Number.isNaN(amount)) totals[date.getMonth()] += amount;
    }

    return totals.map((amount, index) => ({ label: MONTHS[index], amount }));
  }, [transactions, now, currentMonth]);

  const invoicesToPay = useInvoicesToPay();

  return {
    year,
    currentMonth,
    loading,
    budgetsLoading,
    error,
    retry,
    categories,
    items,
    budgetCategories,
    totalPlanned,
    spentBudget,
    spentYear,
    spentMonth,
    monthlyYtd,
    invoicesToPay,
  };
};
